import { isInPast } from '@seen/core/dates'
import type { TaskNewCommentInboxItem } from '@seen/core/models/inbox'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { isProfileResource } from '@seen/core/models/profile'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import { cn, IconComment } from '@seen/ui'
import type { ReactNode } from 'react'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useWarningReporter } from '#src/hooks/useWarningReporter.ts'
import { InboxRow } from '#src/pages/inbox/row/InboxRow.tsx'
import { InboxRowAvatar } from '#src/pages/inbox/row/InboxRowAvatar.tsx'
import { InboxRowContent } from '#src/pages/inbox/row/InboxRowContent.tsx'
import { InboxRowIcon } from '#src/pages/inbox/row/InboxRowIcon.tsx'
import { InboxRowSubtext } from '#src/pages/inbox/row/InboxRowSubtext.tsx'
import { InboxRowText } from '#src/pages/inbox/row/InboxRowText.tsx'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

export interface TaskNewCommentInboxItemRowProps {
  item: TaskNewCommentInboxItem
  notificationItem: NotificationItem
  onSelect?: () => void
  isSelected?: boolean
  sidebarOpen?: boolean
}

export function TaskNewCommentInboxItemRow({
  item,
  notificationItem,
  onSelect,
  isSelected,
  sidebarOpen,
}: TaskNewCommentInboxItemRowProps): ReactNode {
  const { data: task } = useRead(StandardTask, item.task)
  const { data: practitioner } = useRead(
    ExtendedPractitioner,
    isOfType(item.comment.authorReference, 'Practitioner')
      ? item.comment.authorReference
      : undefined
  )
  const { data: patient } = useRead(Participant, task?.participant)
  const { data: order } = useRead(
    Order,
    isOfType(task?.focus, 'ServiceRequest') ? task.focus : undefined
  )

  const validProfile = isProfileResource(item.comment.authorReference)
    ? item.comment.authorReference
    : undefined
  useWarningReporter(
    !validProfile && !!item.comment.authorReference && 'Invalid profile'
  )

  const orderTitle = getOrderTitle(order)
  const isOverdue =
    !!task?.dueOn && !task.isCompletedOrCancelled && isInPast(task.dueOn)

  return (
    <InboxRow
      to={item.actionUrl}
      notificationItem={notificationItem}
      onSelect={onSelect}
      isSelected={isSelected}
      sidebarOpen={sidebarOpen}
      ghost={!!task?.isCompletedOrCancelled}
    >
      <InboxRowAvatar
        profile={validProfile}
        icon={
          <InboxRowIcon>
            <IconComment className="size-2.75" />
          </InboxRowIcon>
        }
      />
      <InboxRowContent>
        <InboxRowText
          notificationItem={notificationItem}
          badges={
            <>
              {patient && (
                <ParticipantTaskRowBadge
                  className="[&>button]:h-[1.55rem] [&>button]:py-0"
                  participant={patient.reference}
                />
              )}
              {isOfType(task?.focus, 'ServiceRequest') && order && (
                <RowBadge
                  loading={!order}
                  icon={
                    <IconOrderStatus
                      status={order?.status}
                      isSubmitting={order?.isSubmitting}
                      className="size-3.5"
                    />
                  }
                >
                  {order?.seenIdentifier} {!!orderTitle && orderTitle}
                </RowBadge>
              )}
            </>
          }
        >
          {task?.priority && (
            <TaskPriorityIcon
              priority={task.priority}
              isTaskCompletedOrCancelled={task.isCompletedOrCancelled}
              className={cn('-mt-0.75 mr-0.75 inline')}
            />
          )}
          {task?.state && (
            <IconTaskState
              state={task.state}
              className="-mt-0.75 mr-0.75 inline"
            />
          )}
          <span>{extractTextFromHTML(task?.title ?? '[deleted]')}</span>
        </InboxRowText>
        <InboxRowSubtext>
          {task?.isCompletedOrCancelled ? (
            task.isDone ? (
              'Completed'
            ) : (
              'Canceled'
            )
          ) : (
            <>
              {isOverdue && (
                <span className="font-medium text-red-600">
                  Overdue &middot;{' '}
                </span>
              )}
              {!!practitioner && (
                <>
                  {practitioner.name}
                  {!!practitioner?.roles?.length && (
                    <> {practitioner?.roles?.join(', ')}</>
                  )}{' '}
                  commented: {extractTextFromHTML(item.comment.text)}
                </>
              )}
              {!practitioner && (
                <>Comment: {extractTextFromHTML(item.comment.text)}</>
              )}
            </>
          )}
        </InboxRowSubtext>
      </InboxRowContent>
    </InboxRow>
  )
}
